import React, { useState, useEffect, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import { orgsAPI, profileAPI, emailAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { Loader2, Upload, Trash2, ImageIcon, User, Building2, CheckCircle2, Mail } from 'lucide-react';

const ACCEPTED = 'image/png,image/jpeg,image/jpg,image/webp,image/svg+xml';
const MAX_MB = 2;

const Settings = () => {
  const { currentOrg, user } = useAuth();

  // Profile
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({ full_name: '', phone: '', job_title: '', years_of_experience: '' });
  const [profileEmail, setProfileEmail] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Logo
  const [logoUrl, setLogoUrl] = useState(null);
  const [loadingLogo, setLoadingLogo] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setLoadingProfile(true);
    profileAPI.get()
      .then((p) => {
        setProfile(p);
        setProfileEmail(p.email || '');
        setProfileForm({
          full_name: p.full_name || '',
          phone: p.phone || '',
          job_title: p.job_title || '',
          years_of_experience: p.years_of_experience != null ? String(p.years_of_experience) : '',
        });
      })
      .catch(() => {})
      .finally(() => setLoadingProfile(false));
  }, []);

  useEffect(() => {
    if (!currentOrg) return;
    setLoadingLogo(true);
    orgsAPI.getLogo(currentOrg.id)
      .then(({ logo_url }) => setLogoUrl(logo_url || null))
      .catch(() => setLogoUrl(null))
      .finally(() => setLoadingLogo(false));
  }, [currentOrg]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const payload = {
        full_name: profileForm.full_name.trim() || null,
        phone: profileForm.phone.trim() || null,
        job_title: profileForm.job_title.trim() || null,
        years_of_experience: profileForm.years_of_experience ? parseInt(profileForm.years_of_experience, 10) : null,
      };
      const updated = await profileAPI.update(payload);
      setProfile(updated);
      toast({ title: 'Profil mis à jour', description: 'Vos informations apparaîtront dans les prochains PDFs.' });
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder le profil.', variant: 'destructive' });
    } finally { setSavingProfile(false); }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({ title: 'Fichier trop lourd', description: `Le logo doit faire moins de ${MAX_MB} Mo.`, variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const { logo_url } = await orgsAPI.uploadLogo(currentOrg.id, file);
      setLogoUrl(logo_url);
      toast({ title: 'Logo mis à jour', description: 'Il apparaîtra dans les prochains PDFs générés.' });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Impossible de téléverser le logo.', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer le logo de l'organisation ?")) return;
    setDeleting(true);
    try {
      await orgsAPI.deleteLogo(currentOrg.id);
      setLogoUrl(null);
      toast({ title: 'Logo supprimé' });
    } catch { toast({ title: 'Erreur', description: 'Impossible de supprimer le logo.', variant: 'destructive' }); }
    finally { setDeleting(false); }
  };

  // ── Mailbox connections ──
  const [connections, setConnections] = useState([]);
  const [loadingConn, setLoadingConn] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const loadConnections = () => {
    setLoadingConn(true);
    emailAPI.listConnections()
      .then(setConnections)
      .catch(() => setConnections([]))
      .finally(() => setLoadingConn(false));
  };
  useEffect(() => { loadConnections(); }, []);

  // Toast on return from the OAuth consent screen (?mailbox=connected|error)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const m = params.get('mailbox');
    if (m === 'connected') { toast({ title: 'Messagerie connectée', description: 'Vous pouvez envoyer des emails depuis votre boîte.' }); loadConnections(); }
    else if (m === 'error') { toast({ title: 'Connexion échouée', description: 'La connexion à la messagerie a échoué.', variant: 'destructive' }); }
    if (m) window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const handleConnectGoogle = async () => {
    setConnecting(true);
    try {
      const { auth_url } = await emailAPI.connectGoogle();
      window.location.href = auth_url; // redirect to Google consent
    } catch (err) {
      toast({ title: 'Indisponible', description: err?.response?.data?.detail || "Google n'est pas encore configuré.", variant: 'destructive' });
      setConnecting(false);
    }
  };

  const handleDisconnect = async (id) => {
    try {
      await emailAPI.disconnect(id);
      setConnections((prev) => prev.filter((c) => c.id !== id));
      toast({ title: 'Messagerie déconnectée' });
    } catch { toast({ title: 'Erreur', description: 'Impossible de déconnecter.', variant: 'destructive' }); }
  };

  const profileComplete = !!(profileForm.full_name && profileForm.phone && profileForm.job_title);

  return (
    <AppLayout title="Paramètres">
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div className="sd-page-head">
          <h1>Paramètres</h1>
          <p>{currentOrg?.name} · {user?.email}</p>
        </div>

        {/* Status row */}
        <div className="sd-stats-row sd-stagger" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="sd-stat-card">
            <div className="sd-stat-label">Profil professionnel</div>
            <div className="sd-stat-value" style={{ fontSize: 18 }}>{profileComplete ? 'Complet' : 'À compléter'}</div>
            <span className={`sd-stat-delta ${profileComplete ? 'up' : 'neutral'}`}>
              {profileComplete ? <><CheckCircle2 size={11} /> prêt</> : 'remplissez les champs'}
            </span>
            <div className="sd-stat-icon" style={{ background: 'rgba(108,71,255,0.1)', color: 'var(--vio2)' }}>
              <User size={16} />
            </div>
          </div>
          <div className="sd-stat-card">
            <div className="sd-stat-label">Logo organisation</div>
            <div className="sd-stat-value" style={{ fontSize: 18 }}>{logoUrl ? 'Configuré' : 'Manquant'}</div>
            <span className={`sd-stat-delta ${logoUrl ? 'up' : 'neutral'}`}>
              {logoUrl ? <><CheckCircle2 size={11} /> visible dans les PDFs</> : 'apparaît dans les PDFs'}
            </span>
            <div className="sd-stat-icon" style={{ background: 'rgba(59,130,246,0.1)', color: '#60A5FA' }}>
              <ImageIcon size={16} />
            </div>
          </div>
          <div className="sd-stat-card">
            <div className="sd-stat-label">Organisation</div>
            <div className="sd-stat-value" style={{ fontSize: 18, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentOrg?.name || '—'}</div>
            <span className="sd-stat-delta neutral">{currentOrg?.slug || ''}</span>
            <div className="sd-stat-icon" style={{ background: 'rgba(34,197,94,0.1)', color: '#4ADE80' }}>
              <Building2 size={16} />
            </div>
          </div>
        </div>

        {/* Profile section */}
        <div className="sd-dash-section" style={{ marginBottom: 16 }}>
          <div className="sd-dash-section-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <User size={14} style={{ color: 'var(--vio2)' }} />
              <div>
                <span className="sd-dash-section-title">Mon profil professionnel</span>
                <p style={{ fontSize: 11, color: 'var(--muted2)', marginTop: 2 }}>
                  Ces informations apparaîtront dans les PDFs générés pour vos dossiers.
                </p>
              </div>
            </div>
          </div>
          <div style={{ padding: '16px 20px 20px' }}>
            {loadingProfile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted2)', fontSize: 12 }}>
                <Loader2 size={14} style={{ animation: 'sd-spin 1s linear infinite' }} /> Chargement…
              </div>
            ) : (
              <form onSubmit={handleSaveProfile} className="sd-form">
                <div className="sd-form-grid">
                  <div className="sd-field">
                    <label>Nom complet</label>
                    <input type="text" className="sd-input" value={profileForm.full_name}
                      onChange={(e) => setProfileForm(f => ({ ...f, full_name: e.target.value }))}
                      placeholder="Jean Dupont" />
                  </div>
                  <div className="sd-field">
                    <label>Email <span className="sd-field-hint">(lecture seule)</span></label>
                    <input type="text" className="sd-input" value={profileEmail} readOnly />
                  </div>
                  <div className="sd-field">
                    <label>Téléphone</label>
                    <input type="tel" className="sd-input" value={profileForm.phone}
                      onChange={(e) => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="+33 6 00 00 00 00" />
                  </div>
                  <div className="sd-field">
                    <label>Titre / poste</label>
                    <input type="text" className="sd-input" value={profileForm.job_title}
                      onChange={(e) => setProfileForm(f => ({ ...f, job_title: e.target.value }))}
                      placeholder="Consultant Senior" />
                  </div>
                  <div className="sd-field">
                    <label>Années d'expérience</label>
                    <input type="number" min={0} max={50} className="sd-input"
                      value={profileForm.years_of_experience}
                      onChange={(e) => setProfileForm(f => ({ ...f, years_of_experience: e.target.value }))}
                      placeholder="5" style={{ maxWidth: 140 }} />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
                  <button type="submit" disabled={savingProfile} className="sd-btn sd-btn-primary">
                    {savingProfile ? <><Loader2 size={13} style={{ animation: 'sd-spin 1s linear infinite' }} /> Sauvegarde…</> : 'Sauvegarder'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Logo section */}
        <div className="sd-dash-section">
          <div className="sd-dash-section-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ImageIcon size={14} style={{ color: 'var(--vio2)' }} />
              <div>
                <span className="sd-dash-section-title">Logo de l'organisation</span>
                <p style={{ fontSize: 11, color: 'var(--muted2)', marginTop: 2 }}>
                  Apparaît sur la page de couverture et l'en-tête des PDFs. PNG, JPG, WebP, SVG — max {MAX_MB} Mo.
                </p>
              </div>
            </div>
          </div>
          <div style={{ padding: '16px 20px 20px' }}>
            {loadingLogo ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted2)', fontSize: 12 }}>
                <Loader2 size={14} style={{ animation: 'sd-spin 1s linear infinite' }} /> Chargement…
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
                <div style={{
                  width: 160, height: 92,
                  border: logoUrl ? '1px solid var(--border2)' : '1.5px dashed rgba(123,104,238,0.35)',
                  borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'var(--surface2)', flexShrink: 0, overflow: 'hidden',
                }}>
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo organisation" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', padding: 8 }} />
                  ) : (
                    <ImageIcon size={28} style={{ color: 'rgba(123,104,238,0.5)' }} />
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input ref={fileInputRef} type="file" accept={ACCEPTED} style={{ display: 'none' }} onChange={handleFileChange} />
                  <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="sd-btn sd-btn-primary">
                    {uploading ? <Loader2 size={13} style={{ animation: 'sd-spin 1s linear infinite' }} /> : <Upload size={13} />}
                    {uploading ? 'Téléversement…' : logoUrl ? 'Remplacer' : 'Téléverser un logo'}
                  </button>
                  {logoUrl && (
                    <button onClick={handleDelete} disabled={deleting} className="sd-btn sd-btn-danger">
                      {deleting ? <Loader2 size={13} style={{ animation: 'sd-spin 1s linear infinite' }} /> : <Trash2 size={13} />}
                      {deleting ? 'Suppression…' : 'Supprimer'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Messagerie section */}
        <div className="sd-dash-section" style={{ marginTop: 16 }}>
          <div className="sd-dash-section-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Mail size={14} style={{ color: 'var(--vio2)' }} />
              <div>
                <span className="sd-dash-section-title">Messagerie</span>
                <p style={{ fontSize: 11, color: 'var(--muted2)', marginTop: 2 }}>
                  Connectez votre boîte pour envoyer des emails aux clients depuis Skilldeck. Les mails partent de votre adresse et apparaissent dans vos « Envoyés ».
                </p>
              </div>
            </div>
          </div>
          <div style={{ padding: '16px 20px 20px' }}>
            {loadingConn ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--muted2)', fontSize: 12 }}>
                <Loader2 size={14} style={{ animation: 'sd-spin 1s linear infinite' }} /> Chargement…
              </div>
            ) : connections.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {connections.map((c) => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', border: '1px solid var(--border)', borderRadius: 9, background: 'var(--surface2)' }}>
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(52,211,153,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#34D399', flexShrink: 0 }}>
                      <CheckCircle2 size={16} />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{c.email_address || 'Boîte connectée'}</div>
                      <div style={{ fontSize: 11, color: 'var(--muted2)', textTransform: 'capitalize' }}>{c.provider === 'google' ? 'Gmail / Google' : c.provider}</div>
                    </div>
                    <button onClick={() => handleDisconnect(c.id)} className="sd-btn sd-btn-danger"><Trash2 size={13} /> Déconnecter</button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={handleConnectGoogle} disabled={connecting} className="sd-btn sd-btn-primary">
                  {connecting ? <Loader2 size={13} style={{ animation: 'sd-spin 1s linear infinite' }} /> : (
                    <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true"><path fill="#fff" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" opacity=".9"/><path fill="#fff" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" opacity=".7"/></svg>
                  )}
                  Connecter Gmail
                </button>
                <button
                  onClick={() => toast({ title: 'Bientôt', description: 'La connexion Outlook arrive juste après Gmail.' })}
                  className="sd-btn"
                  style={{ border: '1px solid var(--border)', background: 'transparent', color: 'var(--muted2)' }}
                >
                  Connecter Outlook
                  <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#FB923C', background: 'rgba(249,115,22,0.15)', padding: '1px 6px', borderRadius: 10 }}>Bientôt</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default Settings;
